import { useQuery } from "@tanstack/react-query";

import { supabase } from "@/shared/lib/supabase";
import { buildAddress } from "@/shared/utils/addressUtils";

export interface EstateAddressOption {
  addressId: number;
  label: string;
}

interface AddressRow {
  id: number;
  street1: string | null;
  street2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  zip4: string | null;
}

function toLabel(address: AddressRow): string {
  const zip = address.zip4 ? `${address.zip ?? ""}-${address.zip4}` : (address.zip ?? "");
  return buildAddress({
    streetAddress: address.street1 ?? "",
    apartment: address.street2 ?? "",
    city: address.city ?? "",
    state: address.state ?? "",
    zipCode: zip,
  }).trim();
}

export function useEstateAddresses(estateId: number | undefined) {
  return useQuery({
    queryKey: ["estateAddresses", estateId],
    queryFn: async (): Promise<EstateAddressOption[]> => {
      const { data, error } = await supabase.functions.invoke("account/estate-addresses", {
        body: { estateId },
      });
      if (error) throw error;

      const rows = (data as { addresses?: AddressRow[] } | null)?.addresses ?? [];
      return rows
        .map((address) => ({ addressId: address.id, label: toLabel(address) }))
        .filter((option) => option.label);
    },
    enabled: !!estateId,
  });
}
